import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/common.css';

function ChildRecords() {
  const [user, setUser] = useState(null);
  const [records, setRecords] = useState({
    weeklyStats: { completed: 0, total: 0, completionRate: 0 },
    monthlyStats: { completed: 0, total: 0, completionRate: 0 },
    recentMissions: [],
    achievements: {
      continuousStreak: 0,
      perfectWeeks: 0,
      totalCompleted: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/child/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'child') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      fetchRecordsData();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/child/login');
    }
  }, [navigate]);

  const fetchRecordsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      let url = '/api/v0/reports/child/my-records';

      // 부모 계정으로 접속한 경우 첫 번째 자녀의 기록을 가져옴
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.type === 'parent') {
          // 부모인 경우 자녀 목록을 먼저 가져와서 첫 번째 자녀 ID 사용
          const childrenResponse = await fetch('/api/v0/families/me/children', {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (childrenResponse.ok) {
            const childrenData = await childrenResponse.json();
            if (childrenData.children && childrenData.children.length > 0) {
              url += `?childId=${childrenData.children[0].id}`;
            } else {
              setRecords({
                weeklyStats: { completed: 0, total: 0, completionRate: 0 },
                monthlyStats: { completed: 0, total: 0, completionRate: 0 },
                recentMissions: [],
                achievements: { continuousStreak: 0, perfectWeeks: 0, totalCompleted: 0 }
              });
              setLoading(false);
              return;
            }
          }
        }
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRecords({
          weeklyStats: data.weeklyStats,
          monthlyStats: data.monthlyStats,
          recentMissions: data.recentMissions || [],
          achievements: data.achievements || {
            continuousStreak: 0,
            perfectWeeks: 0,
            totalCompleted: 0
          }
        });
      } else {
        console.error('기록 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('기록 데이터 로딩 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const getCompletionColor = (rate) => {
    if (rate >= 80) return '#28a745';
    if (rate >= 60) return '#ffc107';
    return '#dc3545';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'checked': return '⏰';
      default: return '⏳';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return '완료';
      case 'rejected': return '다시 하기';
      case 'checked': return '확인 대기';
      default: return '미완료';
    }
  };

  if (!user) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>📊 내 기록</h1>
        <button onClick={handleLogout} className="btn btn-outline btn-sm">
          로그아웃
        </button>
      </div>

      <div className="page-content">
        {/* 탭 메뉴 */}
        <div className="tab-menu">
          <button
            className={`tab-btn ${activeTab === 'weekly' ? 'active' : ''}`}
            onClick={() => setActiveTab('weekly')}
          >
            이번 주
          </button>
          <button
            className={`tab-btn ${activeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            이번 달
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="stats-card">
          <h3>{activeTab === 'weekly' ? '🗓️ 주간 통계' : '📅 월간 통계'}</h3>
          <div className="stats-content">
            <div className="stats-circle">
              <div
                className="circle-progress"
                style={{
                  background: `conic-gradient(${getCompletionColor(records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate)} 0deg ${records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate * 3.6}deg, #e9ecef ${records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate * 3.6}deg 360deg)`
                }}
              >
                <div className="circle-inner">
                  <span className="completion-rate">
                    {records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate}%
                  </span>
                </div>
              </div>
            </div>
            <div className="stats-info">
              <div className="stat-item">
                <span className="stat-label">완료한 미션</span>
                <span className="stat-value">
                  {records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completed}개
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">전체 미션</span>
                <span className="stat-value">
                  {records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].total}개
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">달성률</span>
                <span className="stat-value" style={{ color: getCompletionColor(records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate) }}>
                  {records[activeTab === 'weekly' ? 'weeklyStats' : 'monthlyStats'].completionRate}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 최근 미션 내역 */}
        <div className="missions-card">
          <h3>🎯 최근 미션 내역</h3>
          {records.recentMissions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>아직 완료한 미션이 없어요</p>
              <p className="empty-subtitle">첫 번째 미션을 완료해보세요!</p>
            </div>
          ) : (
            <div className="missions-list">
              {records.recentMissions.map((mission, index) => (
                <div key={mission.id || index} className={`mission-item ${mission.status}`}>
                  <div className="mission-header">
                    <h4>{mission.title}</h4>
                    <div className="mission-status">
                      {getStatusIcon(mission.status)} {getStatusText(mission.status)}
                    </div>
                  </div>
                  <div className="mission-details">
                    <span className="mission-date">
                      📅 {new Date(mission.completedAt || mission.date).toLocaleDateString()}
                    </span>
                    {mission.status === 'approved' && (
                      <span className="mission-points">
                        +도토리
                      </span>
                    )}
                  </div>
                  {mission.note && (
                    <div className="mission-note">
                      💭 {mission.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 뱃지 및 성취 */}
        <div className="achievements-card">
          <h3>🏆 이번 달 성취</h3>
          <div className="achievements-grid">
            <div className="achievement-item">
              <div className="achievement-icon">🔥</div>
              <div className="achievement-info">
                <h4>연속 달성</h4>
                <p>{records.achievements.continuousStreak}일 연속 완료</p>
              </div>
            </div>
            <div className="achievement-item">
              <div className="achievement-icon">⭐</div>
              <div className="achievement-info">
                <h4>완벽한 주</h4>
                <p>{records.achievements.perfectWeeks}번 달성</p>
              </div>
            </div>
            <div className="achievement-item">
              <div className="achievement-icon">🎯</div>
              <div className="achievement-info">
                <h4>미션 마스터</h4>
                <p>총 {records.achievements.totalCompleted}개 완료</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #764ba2;
          font-size: 1.3rem;
          font-weight: bold;
        }

        .page-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .tab-menu {
          display: flex;
          background: white;
          border-radius: 15px;
          padding: 5px;
          margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .tab-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          background: transparent;
          color: #666;
          border-radius: 10px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .tab-btn.active {
          background: #764ba2;
          color: white;
        }

        .stats-card, .missions-card, .achievements-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .stats-card h3, .missions-card h3, .achievements-card h3 {
          color: #333;
          margin-bottom: 20px;
        }

        .stats-content {
          display: flex;
          align-items: center;
          gap: 30px;
        }

        .stats-circle {
          flex-shrink: 0;
        }

        .circle-progress {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .circle-inner {
          width: 90px;
          height: 90px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .completion-rate {
          font-size: 1.5rem;
          font-weight: bold;
          color: #333;
        }

        .stats-info {
          flex: 1;
        }

        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .stat-item:last-child {
          border-bottom: none;
        }

        .stat-label {
          color: #666;
        }

        .stat-value {
          font-weight: bold;
          color: #333;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .empty-state p {
          margin: 8px 0;
        }

        .empty-subtitle {
          font-size: 0.9rem;
          opacity: 0.7;
        }

        .missions-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mission-item {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 10px;
          border-left: 4px solid #e9ecef;
        }

        .mission-item.approved {
          border-left-color: #28a745;
          background: #f8fff4;
        }

        .mission-item.rejected {
          border-left-color: #dc3545;
          background: #fff5f5;
        }

        .mission-item.checked {
          border-left-color: #ffc107;
          background: #fffbf0;
        }

        .mission-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .mission-header h4 {
          margin: 0;
          color: #333;
          font-size: 1rem;
        }

        .mission-status {
          font-size: 0.9rem;
          font-weight: bold;
        }

        .mission-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .mission-date {
          color: #666;
          font-size: 0.9rem;
        }

        .mission-points {
          color: #28a745;
          font-weight: bold;
          font-size: 0.9rem;
        }

        .mission-note {
          color: #555;
          font-size: 0.9rem;
          font-style: italic;
          padding-top: 10px;
          border-top: 1px solid #e9ecef;
        }

        .achievements-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .achievement-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 10px;
        }

        .achievement-icon {
          font-size: 2rem;
          width: 50px;
          text-align: center;
        }

        .achievement-info h4 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1rem;
        }

        .achievement-info p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        @media (max-width: 480px) {
          .stats-content {
            flex-direction: column;
            gap: 20px;
          }

          .circle-progress {
            width: 100px;
            height: 100px;
          }

          .circle-inner {
            width: 75px;
            height: 75px;
          }

          .completion-rate {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildRecords;